import { type ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAccount } from "wagmi";
import { ConnectButton } from "./ConnectButton";
import { TokenTicker } from "./TokenTicker";
import {
  ArrowLeftRight,
  Droplets,
  Sprout,
  Rocket,
  Link2,
  ExternalLink,
  Menu,
  X,
  Layers,
  BarChart2,
  Copy,
  Lock,
  TrendingUp,
  Gift,
  Plus,
} from "lucide-react";
import { cn } from "../lib/utils";

// Admin wallets — the Cloner nav link only shows for these
const ADMIN_ADDRESSES = [
  "0x49bbefa1d94702c0e9a5eaddec7c3c5d3eb9086b", // TREASURY
  "0x31b8f9a85fa9b9258b5b5f1875dbd863999dca76", // TOKEN_OWNER
];

const NAV_LINKS = [
  { path: "/swap", label: "Swap", icon: ArrowLeftRight },
  { path: "/liquidity", label: "Liquidity", icon: Droplets },
  { path: "/pools", label: "Pools", icon: BarChart2 },
  { path: "/create-pool", label: "Create Pool", icon: Plus },
  { path: "/ve", label: "VE", icon: Lock },
  { path: "/farms", label: "Farms", icon: Sprout },
  { path: "/rewards", label: "Rewards", icon: Gift },
  { path: "/bridge", label: "Bridge", icon: Link2 },
  { path: "/analytics", label: "Analytics", icon: TrendingUp },
  { path: "/launchpad", label: "Launchpad", icon: Rocket },
  { path: "/collection-station", label: "NFT Station", icon: Layers },
  { path: "/token-cloner", label: "Cloner", icon: Copy },
];

function NavLink({
  path,
  label,
  icon: Icon,
  onClick,
}: (typeof NAV_LINKS)[number] & { onClick?: () => void }) {
  const { pathname } = useLocation();
  const isActive = pathname === path || pathname.startsWith(path + "/");

  return (
    <Link
      to={path}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-button text-sm font-medium transition-all duration-200",
        isActive
          ? "text-jasmy bg-jasmy-dim"
          : "text-text-secondary hover:text-text-primary hover:bg-surface-hover"
      )}
      style={
        isActive
          ? { textShadow: "0 0 8px rgba(247,147,26,0.4)" }
          : undefined
      }
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </Link>
  );
}

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { address } = useAccount();
  const isAdmin = !!address && ADMIN_ADDRESSES.includes(address.toLowerCase());
  const navLinks = NAV_LINKS.filter(
    (l) => l.path !== "/token-cloner" || isAdmin
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-[20px] bg-background/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F7931A 0%, #8B2FC9 100%)' }}>
              <span className="font-display font-black text-white text-xs tracking-tight">JS</span>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-black text-lg tracking-wide" style={{ background: 'linear-gradient(90deg, #F7931A, #8B2FC9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Jasmy Swap
              </span>
              <span className="text-[9px] text-text-tertiary tracking-widest uppercase">JasmyChain · 680</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink key={link.path} {...link} />
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <ConnectButton />
          <button
            className="md:hidden p-2 text-text-secondary hover:text-text-primary"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="md:hidden flex flex-col gap-1 px-4 pb-3 animate-fade-in">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              {...link}
              onClick={() => setMobileOpen(false)}
            />
          ))}
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-auto border-t border-border py-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-jasmy flex items-center justify-center">
            <span className="font-display font-bold text-background text-[8px]">J</span>
          </div>
          <span className="text-text-tertiary text-xs">
            Jasmy Swap · JasmyChain 680 · Non-custodial
          </span>
        </div>

        <div className="flex items-center gap-4 text-xs text-text-tertiary flex-wrap justify-end">
          <a
            href="https://jasmy.co.jp/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-jasmy transition-colors"
          >
            Jasmy Official <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://explorer.jasmyscan.net"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-jasmy transition-colors"
          >
            Explorer <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://jasmyscan.net"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-jasmy transition-colors"
          >
            JasmyScan <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://jasmychain.io"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-jasmy transition-colors"
          >
            Docs <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </footer>
  );
}

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  return (
    <>
      <Header />
      <TokenTicker />
      <main className="flex-1">{children}</main>
      <Footer />
    </>
  );
}
